import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { vocabularyService } from '../services/vocabulary';
import { VocabularyCard } from '../types';

const SRSStudy: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cards, setCards] = useState<VocabularyCard[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dueCount, setDueCount] = useState(0);
  const [reviewTime, setReviewTime] = useState(0);

  useEffect(() => {
    loadDueCards();
    
    // Timer for tracking review time
    const interval = setInterval(() => {
      setReviewTime(prev => prev + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const loadDueCards = async () => {
    setLoading(true);
    try {
      const data = await vocabularyService.getDueCards(20);
      setCards(data.cards);
      setDueCount(data.due_count);
    } catch (error) {
      console.error('Error loading cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async (quality: number) => {
    if (cards.length === 0) return;
    
    try {
      await vocabularyService.reviewCard({
        card_id: cards[currentIndex].id,
        quality,
        time_spent_seconds: reviewTime
      });
      
      // Move to next card
      if (currentIndex < cards.length - 1) {
        setCurrentIndex(currentIndex + 1);
        setShowAnswer(false);
        setReviewTime(0);
      } else {
        // Finished all cards
        alert('Great job! You reviewed all due cards! 🎉');
        navigate('/srs');
      }
    } catch (error) {
      console.error('Error reviewing card:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-xl text-gray-600">Loading cards...</div>
      </div>
    );
  }

  if (cards.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <nav className="bg-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
            <button
              onClick={() => navigate('/srs')}
              className="text-indigo-600 hover:text-indigo-800"
            >
              ← Back to SRS
            </button>
          </div>
        </nav>
        
        <div className="flex items-center justify-center h-screen">
          <div className="text-center">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-2xl font-bold mb-2">No cards due!</h2>
            <p className="text-gray-600">Come back later for more practice.</p>
            <button
              onClick={() => navigate('/srs')}
              className="mt-4 bg-indigo-600 text-white px-6 py-2 rounded-lg hover:bg-indigo-700"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentCard = cards[currentIndex];
  const progress = ((currentIndex + 1) / cards.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <button
            onClick={() => navigate('/srs')}
            className="text-indigo-600 hover:text-indigo-800"
          >
            ← Back
          </button>
          <div className="text-center">
            <p className="text-sm text-gray-600">
              Card {currentIndex + 1} of {cards.length}
            </p>
            <div className="w-48 bg-gray-200 rounded-full h-2 mt-1">
              <div 
                className="bg-indigo-600 h-2 rounded-full transition-all"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
          <button
            onClick={() => { logout(); navigate('/login'); }}
            className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
          >
            Logout
          </button>
        </div>
      </nav>

      {/* Card Display */}
      <div className="max-w-2xl mx-auto p-8">
        <div className="bg-white rounded-lg shadow-2xl p-8 min-h-[400px] flex flex-col justify-between">
          {/* Question Side */}
          <div className="flex-1 flex flex-col justify-center items-center">
            {!showAnswer ? (
              <>
                <div className="text-sm text-gray-500 mb-4">
                  {currentCard.category && (
                    <span className="bg-purple-100 text-purple-700 px-3 py-1 rounded-full mr-2">
                      {currentCard.category}
                    </span>
                  )}
                  <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full">
                    {currentCard.difficulty_level}
                  </span>
                </div>
                
                <h2 className="text-5xl font-bold text-indigo-600 mb-8 text-center">
                  {currentCard.english_word}
                </h2>
                
                {currentCard.pronunciation && (
                  <p className="text-gray-500 text-xl mb-4">
                    [{currentCard.pronunciation}]
                  </p>
                )}
                
                <button
                  onClick={() => setShowAnswer(true)}
                  className="mt-8 bg-indigo-600 text-white px-8 py-3 rounded-lg hover:bg-indigo-700 text-lg"
                >
                  Show Answer
                </button>
              </>
            ) : (
              <>
                <h2 className="text-4xl font-bold text-gray-800 mb-4">
                  {currentCard.english_word}
                </h2>
                
                <div className="w-full border-t-2 border-gray-200 my-6"></div>
                
                <h3 className="text-3xl font-bold text-green-600 mb-6">
                  {currentCard.czech_translation}
                </h3>
                
                {currentCard.example_sentence && (
                  <div className="bg-gray-50 p-4 rounded-lg w-full">
                    <p className="text-gray-700 italic">
                      "{currentCard.example_sentence}"
                    </p>
                  </div>
                )}
                
                <div className="mt-8 w-full">
                  <p className="text-center text-gray-600 mb-4 font-semibold">
                    How well did you know it?
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => handleReview(0)}
                      className="bg-red-500 text-white px-4 py-3 rounded-lg hover:bg-red-600"
                    >
                      ❌ Forgot
                    </button>
                    <button
                      onClick={() => handleReview(3)}
                      className="bg-orange-500 text-white px-4 py-3 rounded-lg hover:bg-orange-600"
                    >
                      😕 Hard
                    </button>
                    <button
                      onClick={() => handleReview(4)}
                      className="bg-blue-500 text-white px-4 py-3 rounded-lg hover:bg-blue-600"
                    >
                      👍 Good
                    </button>
                    <button
                      onClick={() => handleReview(5)}
                      className="bg-green-500 text-white px-4 py-3 rounded-lg hover:bg-green-600"
                    >
                      ✨ Easy
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
          
          {/* Stats */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Seen: {currentCard.times_seen} times | Success rate: {
              currentCard.times_seen > 0 
                ? Math.round((currentCard.times_correct / currentCard.times_seen) * 100) 
                : 0
            }%</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SRSStudy;
